import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  CampaignPlanProps,
  GetCampaignPlanResponse,
  ResponseArtifactDTO,
  UpdateResponseArtifactResponse,
  UpdateResponseArtifactRequest,
} from '@dreamai/types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../../api';
import SidewaysLoading from '../../sidebar/SidewaysLoading';
import { ReactComponent as DownCaretIcon } from '../../../../icons/down-caret.svg';
import { ReactComponent as RightCaretIcon } from '../../../../icons/right-caret.svg';
import { ReactComponent as CheckCircleFilledIcon } from '../../../../icons/check-circle-filled.svg';
import { useDependencyUpdates } from '../../../../providers/DependencyUpdateProvider';
import ErrorState from '../../../common/ErrorState';
import ResponseArtifactModal from '../../sidebar/ResponseArtifactModal';
import { isOrganisationChargeError } from '../../sidebar/organisationSpend';
import { isHotelQuoteExpiredError } from '../../sidebar/hotelArtifact';
import AgentStatusPill from '../AgentStatusPill';
import './campaign-plan-component.scss';
import type { CardSelection } from '../../sidebar/SpendingApprovalModal';

interface CampaignPlanComponentProps extends CampaignPlanProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
}

interface Section {
  key: 'goal' | 'messaging' | 'targetCompanies' | 'settings';
  title: string;
  content: string | null;
  state:
    | 'unstarted'
    | 'working-on'
    | 'complete'
    | 'needs-you'
    | 'pending-review';
}

const CampaignPlanComponent: React.FC<CampaignPlanComponentProps> = ({
  campaignPlanId,
  threadId,
  updateThreadViewVisualDescription,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { registerCallback } = useDependencyUpdates();
  const [sections, setSections] = useState<Section[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );
  const [campaignPlanData, setCampaignPlanData] = useState<
    GetCampaignPlanResponse['campaignPlan'] | null
  >(null);

  const getPillInfo = (
    state:
      | 'unstarted'
      | 'working-on'
      | 'complete'
      | 'needs-you'
      | 'pending-review',
  ) => {
    switch (state) {
      case 'complete':
        return { text: 'Done', className: 'status-pill-green' };
      case 'working-on':
        return { text: 'Setting up', className: 'status-pill-blue' };
      case 'needs-you':
        return { text: 'Needs you', className: 'status-pill-red' };
      case 'pending-review':
        return { text: 'Nearly done', className: 'status-pill-beige' };
      case 'unstarted':
        return { text: 'Processing', className: 'status-pill-beige' };
    }
  };

  const fetchCampaignPlan = async () => {
    try {
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get<GetCampaignPlanResponse>(
        `/campaign-plan/${campaignPlanId}`,
      );

      if (response.data.success) {
        const plan = response.data.campaignPlan;
        setCampaignPlanData(plan);
        setSections([
          {
            key: 'goal',
            title: 'Goal',
            content: plan.goal,
            state: plan.goalState,
          },
          {
            key: 'messaging',
            title: 'Messaging',
            content: plan.messaging,
            state: plan.messagingState,
          },
          {
            key: 'targetCompanies',
            title: 'Target Companies',
            content: plan.targetCompanies,
            state: plan.targetCompaniesState,
          },
          {
            key: 'settings',
            title: 'Settings',
            content: plan.settings,
            state: plan.settingsState,
          },
        ]);
      }
    } catch (err) {
      console.error('Error fetching campaign plan:', err);
      setError('Failed to load campaign plan');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaignPlan();
  }, [campaignPlanId, getAccessTokenSilently]);

  // Listen for dependency updates
  useEffect(() => {
    const unregister = registerCallback(
      'campaign_plans',
      campaignPlanId,
      (update) => {
        console.log('Campaign plan updated, reloading:', update);
        fetchCampaignPlan();
      },
    );

    return () => {
      unregister();
    };
  }, [campaignPlanId, registerCallback]);

  // Auto-expand sections that need user attention
  useEffect(() => {
    const needsYouSections = sections
      .filter((section) => section.state === 'needs-you')
      .map((section) => section.key);

    if (needsYouSections.length > 0) {
      setExpandedSections((prev) => {
        const newSet = new Set(prev);
        needsYouSections.forEach((key) => newSet.add(key));
        return newSet;
      });
    }
  }, [sections]);

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const getConfirmationMessage = (sectionKey: string) => {
    if (!campaignPlanData) return null;

    if (
      sectionKey === 'messaging' &&
      campaignPlanData.messagingConfirmations &&
      campaignPlanData.messagingConfirmations.length > 0
    ) {
      return "Please approve the series of emails we'll send to prospects in this campaign";
    }

    if (
      sectionKey === 'targetCompanies' &&
      campaignPlanData.targetCompaniesConfirmations &&
      campaignPlanData.targetCompaniesConfirmations.length > 0
    ) {
      // Check which type of confirmation it is
      const confirmation = campaignPlanData.targetCompaniesConfirmations[0];
      if (confirmation.methodName === 'approveSourcingStrategy') {
        return 'Please approve how the Dreamies will find prospects for this campaign';
      } else if (
        confirmation.methodName === 'approvePendingProspectsForCampaign'
      ) {
        return 'Please approve the list of prospects the Dreamies found for you';
      }
    }

    return null;
  };

  // Artifact modal state
  const [artifactModalOpen, setArtifactModalOpen] = useState(false);
  const [fullArtifact, setFullArtifact] = useState<ResponseArtifactDTO | null>(
    null,
  );
  const [loadingArtifact, setLoadingArtifact] = useState(false);

  const modalArtifacts = useMemo(
    () => (fullArtifact ? [fullArtifact] : []),
    [fullArtifact],
  );

  const handleViewConfirmation = async (sectionKey: string) => {
    if (!campaignPlanData) return;

    let confirmationId: string | null = null;

    if (
      sectionKey === 'messaging' &&
      campaignPlanData.messagingConfirmations?.length
    ) {
      confirmationId = campaignPlanData.messagingConfirmations[0].id;
    } else if (
      sectionKey === 'targetCompanies' &&
      campaignPlanData.targetCompaniesConfirmations?.length
    ) {
      confirmationId = campaignPlanData.targetCompaniesConfirmations[0].id;
    }

    if (!confirmationId) return;

    setLoadingArtifact(true);
    setArtifactModalOpen(true);

    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<{
        success: boolean;
        artifact: ResponseArtifactDTO;
      }>(`/response-artifacts/by-confirmation/${confirmationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.artifact) {
        setFullArtifact(response.data.artifact);
      } else {
        console.error('No artifact found for confirmation:', confirmationId);
        setArtifactModalOpen(false);
      }
    } catch (err) {
      console.error('Failed to fetch artifact for confirmation:', err);
      setArtifactModalOpen(false);
    } finally {
      setLoadingArtifact(false);
    }
  };

  const closeArtifactModal = useCallback(() => {
    setArtifactModalOpen(false);
    setFullArtifact(null);
  }, []);

  const handleSubmitArtifactResponse = useCallback(
    async (
      artifact: ResponseArtifactDTO,
      response: any,
      mandateOptions?: {
        createMandate: boolean;
        mandateItems: Array<{
          actionLine: string;
          descriptionLine: string;
          price?: number;
          price_currency?: string;
        }>;
      },
      cardSelection?: CardSelection,
    ) => {
      try {
        const token = await getAccessTokenSilently();
        const request: UpdateResponseArtifactRequest = {
          artifactId: artifact.id,
          status: 'actioned',
          userResponse: response,
          source: 'chat',
          ...(mandateOptions?.createMandate && {
            create_mandate: true,
            mandate_items: mandateOptions.mandateItems,
          }),
          ...(cardSelection && { card_selection: cardSelection }),
        };
        await api.patch<UpdateResponseArtifactResponse>(
          `/response-artifacts/${artifact.id}`,
          request,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        closeArtifactModal();
        // Refresh the campaign plan to reflect the updated state
        fetchCampaignPlan();
      } catch (error) {
        if (isOrganisationChargeError(error) || isHotelQuoteExpiredError(error))
          throw error;
        console.error('Error submitting artifact response:', error);
      }
    },
    [getAccessTokenSilently, closeArtifactModal],
  );

  const handleViewStartCampaignConfirmation = async () => {
    if (!campaignPlanData?.startCampaignConfirmation) return;

    const confirmationId = campaignPlanData.startCampaignConfirmation.id;

    setLoadingArtifact(true);
    setArtifactModalOpen(true);

    try {
      const token = await getAccessTokenSilently();
      const response = await api.get<{
        success: boolean;
        artifact: ResponseArtifactDTO;
      }>(`/response-artifacts/by-confirmation/${confirmationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.artifact) {
        setFullArtifact(response.data.artifact);
      } else {
        console.error(
          'No artifact found for start campaign confirmation:',
          confirmationId,
        );
        setArtifactModalOpen(false);
      }
    } catch (err) {
      console.error(
        'Failed to fetch artifact for start campaign confirmation:',
        err,
      );
      setArtifactModalOpen(false);
    } finally {
      setLoadingArtifact(false);
    }
  };

  if (loading) {
    return <div className='campaign-plan-component loading'></div>;
  }

  if (error) {
    return <ErrorState title='Failed to load campaign plan' subtitle={error} />;
  }

  return (
    <>
      <div className='campaign-plan-outer'>
        <div className='header-bit'>
          <div className='header-bit-left'>
            <p>Setting up your campaign</p>
            <h3>Campaign Plan</h3>
          </div>
          {threadId && <AgentStatusPill threadId={threadId} />}
        </div>
        <div className='campaign-plan-component'>
          {sections.map((section) => {
            const isExpanded = expandedSections.has(section.key);

            return (
              <div
                key={section.key}
                className={`campaign-plan-section  ${section.state}`}
              >
                <div
                  className='section-header'
                  onClick={() => toggleSection(section.key)}
                >
                  <div className='header-left'>
                    <h3>{section.title}</h3>
                    {section.state === 'working-on' && (
                      <span className='loading-icon'>
                        <SidewaysLoading />
                      </span>
                    )}
                    <span
                      className={`status-pill ${getPillInfo(section.state).className}`}
                    >
                      {getPillInfo(section.state).text}
                    </span>
                    <span className='accordion-icon'>
                      {isExpanded ? <DownCaretIcon /> : <RightCaretIcon />}
                    </span>
                  </div>
                </div>

                {isExpanded && (
                  <>
                    {getConfirmationMessage(section.key) && (
                      <div className='confirmation-alert'>
                        <p>{getConfirmationMessage(section.key)}</p>
                        <button
                          onClick={() => handleViewConfirmation(section.key)}
                        >
                          View
                        </button>
                      </div>
                    )}
                    <div className='section-content'>
                      {section.content ? (
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {section.content}
                        </ReactMarkdown>
                      ) : (
                        <p className='empty-content'>No content yet</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
          {campaignPlanData?.startCampaignConfirmation && (
            <div className='campaign-plan-section start-campaign-section'>
              <div className='start-campaign-content'>
                <CheckCircleFilledIcon className='start-campaign-icon' />
                <div className='start-campaign-text'>
                  <h3>Your campaign is ready to start</h3>
                  <p>
                    Review and confirm to begin sourcing and contacting
                    prospects.
                  </p>
                </div>
                <button
                  className='start-campaign-button'
                  onClick={handleViewStartCampaignConfirmation}
                >
                  Start Campaign
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ResponseArtifactModal
        show={artifactModalOpen && !!fullArtifact && !loadingArtifact}
        onClose={closeArtifactModal}
        artifacts={modalArtifacts}
        initialIndex={0}
        onSubmitResponse={handleSubmitArtifactResponse}
        loading={loadingArtifact}
        readOnly={fullArtifact?.status === 'actioned'}
      />
    </>
  );
};

export default CampaignPlanComponent;
